import { Crown } from 'lucide-react';
import { ACCENT, ACCENT_BRIGHT, ACCENT_HI, ACCENT_LO, ACCENT_TEXT, TEXT_PRIMARY, TEXT_MUTED, PANEL_BG } from '../lib/medievalTheme';
import AvatarImage from './ui/AvatarImage';
import { t } from '../localization/t';

// Circular avatar medallion with a thin gold rim and a small Lucide
// crown icon centered above the portrait. The crown sits ABOVE the
// disc. Not clipped to its edge. So the badge looks intentional at
// any size and survives parent containers that use `overflow:hidden`
// (the strip uses overflow-x-auto, which previously sheared the
// corner glyph). Caller controls the outer size via `size` (any
// CSS length); everything inside scales relative to that value.
export default function AvatarCrownBadge({
  crown,
  size = '2.5cqw',
  showTooltip = true,
  onMouseEnter,
  onMouseLeave,
  ariaLabel,
  active = false,
}) {
  if (!crown) return null;
  // Founder ("Herald of First Dawn") is rendered through the same
  // medallion frame as a crown but with the engraved viking disc
  // standing in for the portrait. No top glyph. The disc art is
  // distinctive enough on its own; an extra icon read as clutter
  // against the figurative engraving.
  const isFounder = crown.kind === 'founder';
  // QA ("Warden of the Proving Grounds") follows the founder recipe.
  // engraved art instead of a portrait, no top glyph. But with the
  // Ringerike beast sitting wholly inside the disc rather than breaking
  // the silhouette, so the two role badges stay tellable apart at
  // strip size.
  const isQa = crown.kind === 'qa';
  const isEmblem = isFounder || isQa;
  const title = showTooltip
    ? (isEmblem
        ? crown.label
        : crown.wins == null
          ? crown.label
          : crown.wins === 1
            ? t('{label}. 1 win', { label: crown.label })
            : t('{label}. {n} wins', { label: crown.label, n: crown.wins }))
    : undefined;
  // The medallion's box-shadow stack is the entire visual depth
  // recipe: a tight black ring under the gold rim for the etched
  // edge, a warm radial glow for the magical aura, an inner bevel
  // for dimensional polish, and (when active) an additional gold
  // outline + much brighter glow that pops the selected crown out
  // of the strip. All shadow rings sit on the circular disc itself
  // so the highlight stays perfectly round. Applying outline to
  // the outer wrapper would have rendered an oval because the
  // wrapper includes the crown glyph sitting above the disc.
  const diskShadow = active
    ? [
        `0 0 0 1px rgba(0,0,0,0.65)`,
        `0 0 0 3px ${ACCENT_TEXT}`,
        `0 0 0 5px rgba(20,14,6,0.95)`,
        `0 0 24px rgba(232, 196, 102, 0.65)`,
        `inset 0 2px 0 rgba(255, 235, 175, 0.35)`,
        `inset 0 -2px 4px rgba(0,0,0,0.55)`,
      ].join(', ')
    : [
        `0 0 0 1px rgba(0,0,0,0.6)`,
        `0 0 12px ${ACCENT_BRIGHT}0.32)`,
        `inset 0 1px 0 rgba(255, 235, 175, 0.22)`,
        `inset 0 -1px 3px rgba(0,0,0,0.45)`,
      ].join(', ');
  return (
    <span
      className="relative inline-flex shrink-0 items-end justify-center"
      style={{
        width: size,
        // Both badge types reserve a strip of headroom above the disc.
        // Crown badges use it for the Crown glyph; founder badges let
        // the viking figure's head peek out of the medallion into that
        // strip for a 3D "carved relief" look.
        height: `calc(${size} * 1.25)`,
        paddingTop: `calc(${size} * 0.25)`,
      }}
      title={title}
      aria-label={ariaLabel || crown.label}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {isEmblem ? null : (
        <Crown
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: 0,
            left: '50%',
            transform: `translateX(-50%) ${active ? 'scale(1.1)' : 'scale(1)'}`,
            width: `calc(${size} * 0.42)`,
            height: `calc(${size} * 0.42)`,
            color: active ? `${ACCENT_HI} 1)` : ACCENT_TEXT,
            fill: active ? `${ACCENT_HI} 1)` : ACCENT_TEXT,
            stroke: 'rgba(0,0,0,0.7)',
            strokeWidth: 1.5,
            filter: active
              ? 'drop-shadow(0 1px 3px rgba(0,0,0,0.85)) drop-shadow(0 0 6px rgba(255, 217, 122, 0.55))'
              : 'drop-shadow(0 1px 2px rgba(0,0,0,0.8))',
            transition: 'transform 160ms ease, filter 160ms ease',
          }}
        />
      )}
      <span
        className="relative block rounded-full overflow-hidden"
        style={{
          width: size,
          height: size,
          // Founder / QA disc: deep ink background behind the engraved
          // art so the gold linework reads crisply. Portrait badges
          // keep the soft dark wash that the cover art sits on top of.
          background: isEmblem ? '#0a0805' : 'rgba(0,0,0,0.4)',
          border: `1px solid ${active ? `${ACCENT_HI} 1)` : ACCENT_TEXT}`,
          boxShadow: diskShadow,
          transition: 'box-shadow 160ms ease, border-color 160ms ease',
        }}
      >
        {isQa ? (
          // Ringerike-style beast, gold-on-transparent like the founder
          // engraving. Sized by WIDTH because this art is landscape
          // (1.18:1). A 1.18:1 rectangle inscribed in the disc maxes
          // out at 0.76 × diameter, so 0.74 leaves rim clearance on the
          // wide axis, which is the one that binds here.
          <img
            src="/ornaments/viking-ringerike-018.png"
            alt=""
            draggable={false}
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: `translate(-50%, -50%) ${active ? 'scale(1.04)' : 'scale(1)'}`,
              width: `calc(${size} * 0.74)`,
              height: 'auto',
              pointerEvents: 'none',
              filter: active
                ? 'drop-shadow(0 0 6px rgba(255, 217, 122, 0.55))'
                : 'drop-shadow(0 1px 3px rgba(0,0,0,0.7))',
              transition: 'transform 160ms ease, filter 160ms ease',
            }}
          />
        ) : isFounder ? null /* see overflowing viking layer below */ : crown.image ? (
          <AvatarImage src={crown.image} className="w-full h-full" />
        ) : (
          <span
            className="w-full h-full flex items-center justify-center text-[0.625cqw] font-bold"
            style={{ color: TEXT_MUTED }}
          >
            {crown.avatarName?.[0]?.toUpperCase() || '?'}
          </span>
        )}
      </span>
      {isFounder ? (() => {
        // Engraved viking figure rendered AS the medallion's relief.
        //
        // The PNG is gold-on-transparent (alpha = source luminance,
        // RGB baked to a gold ramp), so there's no black square to
        // blend away. It lays cleanly over both the disc's deep-ink
        // fill and the cover-art above.
        //
        // The mask is a "tombstone" shape: a central head-width
        // rectangle on top of the disc's circle. The rectangle's
        // bottom corners land exactly on the circle (x=±25 from
        // center at y=31.7 are both on a 50-radius circle centered
        // at (50,75). Disc's actual geometry in wrapper coords),
        // so the union is smooth. The mask clips everything outside
        // the figure-extension+disc region. Feet, cloak edges, wing
        // tips that overshoot the disc on the sides are cropped to
        // the medallion's rim, and only the head breaks the
        // silhouette upward into the headroom strip.
        const maskUri = "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 125' preserveAspectRatio='none'><path d='M 25 0 L 75 0 L 75 31.7 A 50 50 0 1 1 25 31.7 Z' fill='white'/></svg>\")";
        return (
          <span
            aria-hidden="true"
            className="absolute inset-0 pointer-events-none"
            style={{
              maskImage: maskUri,
              WebkitMaskImage: maskUri,
              maskSize: '100% 100%',
              WebkitMaskSize: '100% 100%',
              maskRepeat: 'no-repeat',
              WebkitMaskRepeat: 'no-repeat',
            }}
          >
            <img
              src="/ornaments/viking-figurative-027.png"
              alt=""
              draggable={false}
              style={{
                position: 'absolute',
                bottom: 0,
                left: '50%',
                transform: `translateX(-50%) ${active ? 'scale(1.04)' : 'scale(1)'}`,
                width: `calc(${size} * 0.95)`,
                height: 'auto',
                pointerEvents: 'none',
                filter: active
                  ? 'drop-shadow(0 0 6px rgba(255, 217, 122, 0.55))'
                  : 'drop-shadow(0 1px 3px rgba(0,0,0,0.7))',
                transition: 'transform 160ms ease, filter 160ms ease',
              }}
            />
          </span>
        );
      })() : null}
    </span>
  );
}

// Horizontal strip of crown badges. The currently-highlighted crown
// drives the title shown next to the strip; hovering a badge previews
// that crown's title locally. The caller is the source of truth for
// which crown is selected so the title text and the strip stay in
// sync. Vertical padding leaves room for the crown glyph sitting
// above each medallion.
export function CrownStrip({ crowns, selectedIndex, onHover, onLeave, onSelect, selectable = false }) {
  if (!crowns?.length) return null;
  return (
    <div
      className="flex items-end gap-2 overflow-x-auto overflow-y-visible py-1 pr-2"
    >
      {crowns.map((crown, i) => {
        const active = i === selectedIndex;
        return (
          <button
            key={crown.avatarId}
            type="button"
            aria-pressed={selectable ? active : undefined}
            className="relative cursor-pointer transition-transform"
            style={{
              transform: active ? 'scale(1.06)' : 'scale(1)',
              padding: 0,
              background: 'transparent',
              border: 'none',
              outline: 'none',
            }}
            onMouseEnter={() => onHover?.(i)}
            onMouseLeave={() => onLeave?.()}
            onClick={() => onSelect?.(i)}
          >
            <AvatarCrownBadge crown={crown} size="2.25cqw" />
          </button>
        );
      })}
    </div>
  );
}

// Subtle panel-wrapped section used inside FriendProfileOverlay's
// hero banner. Gives the crown strip + title visual weight without
// competing with the avatar / rank chip. In picker mode, badges
// double as radio-buttons that update the player's chosen displayed
// crown via onPick.
export function CrownSection({
  crowns,
  selectedIndex,
  onHover,
  onLeave,
  onSelect,
  displayedCrown,
  pickerMode = false,
  pickedAvatarId = null,
  onPick,
}) {
  if (!crowns?.length) return null;
  // Featured crown. What the section's hero portrait and centered
  // label/title block render. Falls back through caller's chosen
  // displayedCrown → currently-hovered → first.
  const featured = displayedCrown || crowns[selectedIndex] || crowns[0];
  const multiple = crowns.length > 1;

  return (
    <div
      className="px-5 py-4 rounded-lg flex flex-col items-center gap-3"
      style={{
        background: PANEL_BG,
        border: `1px solid ${ACCENT} 0.18)`,
        boxShadow: 'inset 0 0 22px rgba(0,0,0,0.35)',
      }}
    >
      {/* Section banner. Centered label between horizontal gold rules,
          the same recipe the other ceremonial panels use (achievement
          overlay, dice modal). The optional count sits inline so the
          eye reads "Crowns · 3" as a single beat. In picker mode the
          "None" affordance lives in the top-right corner so it doesn't
          compete with the banner. */}
      <div className="relative flex items-center gap-3 w-full">
        <div className="flex-1 h-px" style={{ background: `linear-gradient(90deg, transparent 0%, ${ACCENT} 0.45) 100%)` }} />
        <span className="text-[0.5625cqw] font-semibold" style={{ color: ACCENT_TEXT, textShadow: `0 0 8px ${ACCENT} 0.35)` }}>
          {multiple ? t('Crowns') : t('Crown')}
          {!pickerMode && multiple ? (
            <span className="ml-1.5 tabular-nums" style={{ color: TEXT_MUTED }}>· {crowns.length}</span>
          ) : null}
        </span>
        <div className="flex-1 h-px" style={{ background: `linear-gradient(90deg, ${ACCENT} 0.45) 0%, transparent 100%)` }} />
        {pickerMode ? (
          <button
            type="button"
            onClick={() => onPick?.(null)}
            className="absolute right-0 -top-0.5 text-[0.5625cqw] cursor-pointer transition-colors px-1.5 py-0.5 rounded"
            style={{
              color: pickedAvatarId == null ? ACCENT_TEXT : TEXT_MUTED,
              background: pickedAvatarId == null ? `${ACCENT} 0.1)` : 'transparent',
              border: 'none',
            }}
          >
            {t('None')}
          </button>
        ) : null}
      </div>

      {/* Featured crown. Hero portrait, centered. Larger than the
          strip badges below so the eye locks on it first. Clicking the
          hero portrait is a no-op outside picker mode; in picker mode
          it acts on the featured crown. */}
      <button
        type="button"
        aria-pressed={pickerMode ? pickedAvatarId === featured?.avatarId : undefined}
        className="relative cursor-pointer transition-transform"
        style={{
          transform: 'scale(1)',
          padding: 0,
          background: 'transparent',
          border: 'none',
          borderRadius: '50%',
          outline: pickerMode && pickedAvatarId === featured?.avatarId ? `2px solid ${ACCENT_TEXT}` : 'none',
          outlineOffset: '3px',
          cursor: pickerMode ? 'pointer' : 'default',
        }}
        onClick={() => {
          if (pickerMode && featured) onPick?.(featured.avatarId);
        }}
      >
        <AvatarCrownBadge crown={featured} size="3.5cqw" showTooltip={false} />
      </button>

      {/* Featured crown text. Centered, gold-gradient label so the
          name reads as struck metal. Title (e.g. "Sower of Conquest")
          gets the italic accent-gold treatment from the dice/precon
          banners; win count stays muted. */}
      {featured ? (
        <div className="flex flex-col items-center text-center" style={{ gap: '0.18cqw' }}>
          <span
            className="text-[0.9375cqw] font-bold"
            style={{
              background: `linear-gradient(180deg, ${ACCENT_HI} 1) 0%, ${ACCENT_BRIGHT} 1) 55%, ${ACCENT_LO} 1) 100%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              letterSpacing: '0.02em',
              filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.6))',
            }}
          >
            {featured.label}
          </span>
          {featured.title ? (
            <span className="text-[0.6875cqw] italic" style={{ color: ACCENT_TEXT, letterSpacing: '0.04em' }}>
              {featured.title}
            </span>
          ) : null}
          <span className="text-[0.5625cqw] tabular-nums" style={{ color: TEXT_MUTED }}>
            {featured.wins === 1 ? t('1 win') : t('{n} wins', { n: featured.wins })}
          </span>
        </div>
      ) : null}

      {/* Strip of other crowns. Hidden when there's only one so the
          single-crown view stays clean. When shown, lives below a
          subtle horizontal rule + centered with `justify-center` so
          the badges don't pile up against the left edge. */}
      {multiple ? (
        <>
          <div className="w-full h-px" style={{ background: `linear-gradient(90deg, transparent 0%, ${ACCENT} 0.25) 50%, transparent 100%)` }} />
          <div className="flex items-end justify-center flex-wrap gap-2.5 w-full pt-1">
            {crowns.map((crown, i) => {
              const active = featured && crown.avatarId === featured.avatarId;
              const picked = pickerMode && pickedAvatarId === crown.avatarId;
              return (
                <button
                  key={crown.avatarId}
                  type="button"
                  aria-pressed={pickerMode ? picked : undefined}
                  className="relative cursor-pointer transition-transform"
                  style={{
                    transform: active || picked ? 'scale(1.08)' : 'scale(1)',
                    padding: 0,
                    background: 'transparent',
                    border: 'none',
                    outline: picked ? `2px solid ${ACCENT_TEXT}` : 'none',
                    outlineOffset: '2px',
                    borderRadius: '50%',
                    opacity: active ? 1 : 0.7,
                  }}
                  onMouseEnter={() => onHover?.(i)}
                  onMouseLeave={() => onLeave?.()}
                  onClick={() => (pickerMode ? onPick?.(crown.avatarId) : onSelect?.(i))}
                >
                  <AvatarCrownBadge crown={crown} size="1.875cqw" />
                </button>
              );
            })}
          </div>
        </>
      ) : null}
    </div>
  );
}
